import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import {
  getTransactionHistory,
  getWithdrawHistory,
} from "../../store/slices/user";
import styles from "./DWHistory.module.css";

const PAGE_SIZE = 10;

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const num = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(num) ? num : null;
};

const fromMeta = (meta, keys) => {
  if (!meta || typeof meta !== "object") return null;
  for (const key of keys) {
    const parsed = toNumber(meta[key]);
    if (parsed !== null) return parsed;
  }
  if (meta.pagination && typeof meta.pagination === "object") {
    const nested = fromMeta(meta.pagination, keys);
    if (nested !== null) return nested;
  }
  if (meta.meta && typeof meta.meta === "object") {
    const nested = fromMeta(meta.meta, keys);
    if (nested !== null) return nested;
  }
  return null;
};

const extractTotalFromMeta = (meta) =>
  fromMeta(meta, [
    "total",
    "totalCount",
    "count",
    "totalRecords",
    "totalItems",
    "recordsTotal",
  ]);

const extractLimitFromMeta = (meta) =>
  fromMeta(meta, ["limit", "pageSize", "perPage", "page_length"]);

export default function DWHistory() {
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [depositLoaded, setDepositLoaded] = useState(false);
  const [withdrawLoaded, setWithdrawLoaded] = useState(false);
  const [depositPage, setDepositPage] = useState(1);
  const [withdrawPage, setWithdrawPage] = useState(1);

  const dispatch = useDispatch();
  const { user } = useAuth();
  const userId = user?.id || user?.userId || user?.user_id;

  const transactionHistory = useSelector(
    (s) => s.user?.transactionHistory || [],
  );
  const withdrawHistory = useSelector((s) => s.user?.withdrawHistory || []);
  const transactionMeta = useSelector(
    (s) => s.user?.transactionHistoryMeta || null,
  );
  const withdrawMeta = useSelector((s) => s.user?.withdrawHistoryMeta || null);

  /* ── Lazy load deposit on accordion open ── */
  const toggleDeposit = useCallback(() => {
    setDepositOpen((prev) => {
      const next = !prev;
      if (next && !depositLoaded) setDepositLoaded(true);
      return next;
    });
  }, [depositLoaded]);

  const toggleWithdraw = useCallback(() => {
    setWithdrawOpen((prev) => {
      const next = !prev;
      if (next && !withdrawLoaded) setWithdrawLoaded(true);
      return next;
    });
  }, [withdrawLoaded]);

  useEffect(() => {
    if (!depositLoaded || !userId) return;
    dispatch(
      getTransactionHistory({
        id: userId,
        page: depositPage,
        limit: PAGE_SIZE,
        type: "deposit",
      }),
    );
  }, [depositLoaded, userId, dispatch, depositPage]);

  useEffect(() => {
    if (!withdrawLoaded || !userId) return;
    dispatch(
      getWithdrawHistory({ id: userId, page: withdrawPage, limit: PAGE_SIZE }),
    );
  }, [withdrawLoaded, userId, dispatch, withdrawPage]);

  /* ── Formatters ── */
  const formatDate = (value) => {
    if (!value) return "-";
    return moment(value).add(543, "year").format("DD/MM/YY HH:mm");
  };

  const formatAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const mapStatus = (status, fallback) => {
    if (!status) return fallback || "-";
    const text = String(status).toUpperCase();
    switch (text) {
      case "SUCCESS":
      case "APPROVED":
      case "COMPLETE":
        return "สำเร็จ";
      case "PENDING":
      case "CHECKING":
      case "WAITING":
        return "รอดำเนินการ";
      case "REJECT":
      case "FAILED":
      case "CANCEL":
      case "CANCELLED":
        return "ไม่สำเร็จ";
      default:
        return status;
    }
  };

  const statusClass = (status) => {
    if (!status) return "";
    const text = String(status).toUpperCase();
    if (["SUCCESS", "APPROVED", "COMPLETE"].includes(text))
      return styles.statusSuccess;
    if (["PENDING", "CHECKING", "WAITING"].includes(text))
      return styles.statusPending;
    if (["REJECT", "FAILED", "CANCEL", "CANCELLED"].includes(text))
      return styles.statusFailed;
    return "";
  };

  const describeDepositChannel = (item) => {
    const ch =
      item?.channel || item?.platform || item?.provider || item?.method;
    if (ch) return ch;
    const type = String(item?.topupType || "").toUpperCase();
    if (type === "MANUALDEPOSIT") return "แมนนวล";
    if (type === "DEPOSIT") return "อัตโนมัติ";
    return item?.topupType || "-";
  };

  const describeWithdrawChannel = (item) =>
    item?.channel || item?.bankName || item?.provider || item?.method || "-";

  /* ── Deposit data ── */
  const depositRows = useMemo(() => {
    if (!Array.isArray(transactionHistory)) return [];
    return transactionHistory
      .filter((i) => {
        const t = String(i?.topupType || "").toUpperCase();
        return t === "DEPOSIT" || t === "MANUALDEPOSIT";
      })
      .slice(0, PAGE_SIZE);
  }, [transactionHistory]);

  const withdrawRows = useMemo(() => {
    if (!Array.isArray(withdrawHistory)) return [];
    return withdrawHistory.slice(0, PAGE_SIZE);
  }, [withdrawHistory]);

  /* ── Pagination helpers ── */
  const dTotal = extractTotalFromMeta(transactionMeta);
  const dLimit = extractLimitFromMeta(transactionMeta) || PAGE_SIZE;
  const dPages =
    dTotal !== null && dLimit > 0
      ? Math.max(1, Math.ceil(dTotal / dLimit))
      : null;
  const dHasPrev = depositPage > 1;
  const dHasNext =
    dPages !== null ? depositPage < dPages : depositRows.length >= dLimit;

  const wTotal = extractTotalFromMeta(withdrawMeta);
  const wLimit = extractLimitFromMeta(withdrawMeta) || PAGE_SIZE;
  const wPages =
    wTotal !== null && wLimit > 0
      ? Math.max(1, Math.ceil(wTotal / wLimit))
      : null;
  const wHasPrev = withdrawPage > 1;
  const wHasNext =
    wPages !== null ? withdrawPage < wPages : withdrawRows.length >= wLimit;

  /* ── Render helpers ── */
  const renderRow = (item, type) => {
    const isDeposit = type === "deposit";
    const key =
      item?.topupHistoryID || item?.withdrawRequestID || item?.id || item?.uuid;
    const date = formatDate(item?.created_at || item?.createdAt);
    const channel = isDeposit
      ? describeDepositChannel(item)
      : describeWithdrawChannel(item);
    const amount = formatAmount(
      isDeposit ? item?.amount : item?.approveAmount || item?.amount,
    );
    const st = mapStatus(item?.status, isDeposit ? "สำเร็จ" : undefined);
    const stCls = statusClass(
      item?.status || (isDeposit ? "SUCCESS" : undefined),
    );

    return (
      <div className={styles.txRow} key={key}>
        <div className={styles.txLeft}>
          <div
            className={`${styles.txIcon} ${isDeposit ? styles.txIconDeposit : styles.txIconWithdraw}`}
          >
            <Icon
              icon={isDeposit ? "mdi:arrow-down-bold" : "mdi:arrow-up-bold"}
              width={16}
            />
          </div>
          <div className={styles.txMeta}>
            <span className={styles.txChannel}>{channel}</span>
            <span className={styles.txDate}>{date}</span>
          </div>
        </div>
        <div className={styles.txRight}>
          <span
            className={`${styles.txAmount} ${isDeposit ? styles.txAmountDeposit : styles.txAmountWithdraw}`}
          >
            {isDeposit ? "+" : "-"}
            {amount}
          </span>
          <span className={`${styles.txStatus} ${stCls}`}>{st}</span>
        </div>
      </div>
    );
  };

  const renderPagination = (
    page,
    totalPages,
    hasPrev,
    hasNext,
    onPrev,
    onNext,
  ) => (
    <div className={styles.pagination}>
      <button className={styles.pageBtn} disabled={!hasPrev} onClick={onPrev}>
        <Icon icon="mdi:chevron-left" width={16} /> ก่อนหน้า
      </button>
      <span className={styles.pageText}>
        หน้า {page}
        {totalPages ? ` / ${totalPages}` : ""}
      </span>
      <button className={styles.pageBtn} disabled={!hasNext} onClick={onNext}>
        ถัดไป <Icon icon="mdi:chevron-right" width={16} />
      </button>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      {/* ── Deposit Accordion ── */}
      <div className={styles.accordion}>
        <button
          className={`${styles.accHeader} ${depositOpen ? styles.accOpen : ""}`}
          onClick={toggleDeposit}
        >
          <div className={styles.accLeft}>
            <div className={`${styles.accIcon} ${styles.accIconDeposit}`}>
              <Icon icon="mdi:arrow-down-bold-circle" width={20} />
            </div>
            <div className={styles.accLabel}>
              <span className={styles.accTitle}>รายการฝากเงิน</span>
              <span className={styles.accHint}>
                กดเพื่อดูประวัติการเติมเงินทั้งหมด
              </span>
            </div>
          </div>
          <Icon
            icon="mdi:chevron-down"
            width={22}
            className={`${styles.accChevron} ${depositOpen ? styles.accChevronOpen : ""}`}
          />
        </button>

        <div
          className={`${styles.accBody} ${depositOpen ? styles.accBodyOpen : ""}`}
        >
          {depositLoaded && (
            <div className={styles.accContent}>
              {depositRows.length === 0 ? (
                <div className={styles.emptyState}>
                  <Icon icon="solar:inbox-line-bold-duotone" width={32} className={styles.emptyIcon} />
                  <span className={styles.emptyText}>
                    ยังไม่มีรายการฝากเงิน
                  </span>
                </div>
              ) : (
                <>
                  {depositRows.map((item) => renderRow(item, "deposit"))}
                  {renderPagination(
                    depositPage,
                    dPages,
                    dHasPrev,
                    dHasNext,
                    () => setDepositPage((p) => Math.max(1, p - 1)),
                    () => setDepositPage((p) => p + 1),
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Withdraw Accordion ── */}
      <div className={styles.accordion}>
        <button
          className={`${styles.accHeader} ${withdrawOpen ? styles.accOpen : ""}`}
          onClick={toggleWithdraw}
        >
          <div className={styles.accLeft}>
            <div className={`${styles.accIcon} ${styles.accIconWithdraw}`}>
              <Icon icon="mdi:arrow-up-bold-circle" width={20} />
            </div>
            <div className={styles.accLabel}>
              <span className={styles.accTitle}>รายการถอนเงิน</span>
              <span className={styles.accHint}>
                กดเพื่อดูประวัติการถอนเงินทั้งหมด
              </span>
            </div>
          </div>
          <Icon
            icon="mdi:chevron-down"
            width={22}
            className={`${styles.accChevron} ${withdrawOpen ? styles.accChevronOpen : ""}`}
          />
        </button>

        <div
          className={`${styles.accBody} ${withdrawOpen ? styles.accBodyOpen : ""}`}
        >
          {withdrawLoaded && (
            <div className={styles.accContent}>
              {withdrawRows.length === 0 ? (
                <div className={styles.emptyState}>
                  <Icon icon="solar:inbox-line-bold-duotone" width={32} className={styles.emptyIcon} />
                  <span className={styles.emptyText}>
                    ยังไม่มีรายการถอนเงิน
                  </span>
                </div>
              ) : (
                <>
                  {withdrawRows.map((item) => renderRow(item, "withdraw"))}
                  {renderPagination(
                    withdrawPage,
                    wPages,
                    wHasPrev,
                    wHasNext,
                    () => setWithdrawPage((p) => Math.max(1, p - 1)),
                    () => setWithdrawPage((p) => p + 1),
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
